import React, { useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { ClaimTopUp } from '../../Client/ManagementServiceClient';
import { useWallet } from '../../hooks/useWallet';
import './styles.css';

/**
 * Handles the redirect back from a Stripe top-up.
 *
 * The redirect is NOT proof of payment and does not credit anything -- the
 * signed webhook does that, out of band. So this only acknowledges the return
 * and polls until the new balance shows up, which is also the honest thing to
 * display: the money is confirmed by Stripe a beat after the browser comes
 * back, and claiming success before then would sometimes be a lie.
 *
 * Locally there is no webhook to wait for -- Stripe cannot call a laptop -- so
 * the poll would run out and leave a real payment sitting at a zero balance.
 * There, and only there, the session id is handed to the server to check with
 * Stripe directly (see /User/Wallet/TopUp/Claim). The endpoint does not exist
 * in deployed environments, so this stays a local convenience and the webhook
 * stays the authority.
 */

const POLL_ATTEMPTS = 10;
const POLL_DELAY_MS = 1500;

function TopUpReturnHandler() {
    const { refresh, localCreditEnabled, loading } = useWallet();
    const [state, setState] = useState(null); // 'confirming' | 'done' | 'slow' | 'cancelled'
    // What came back in the URL, captured on mount because the query string is
    // cleared immediately so a refresh cannot replay it.
    const [ret, setRet] = useState(null);     // { topup, sessionId } | null

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const topup = params.get('topup');
        if (!topup) return;

        const sessionId = params.get('session_id') || '';

        params.delete('topup');
        params.delete('session_id');
        const query = params.toString();
        window.history.replaceState(
            {},
            '',
            window.location.pathname + (query ? `?${query}` : '')
        );

        if (topup === 'cancel') {
            setState('cancelled');
            return;
        }

        setState('confirming');
        setRet({ topup, sessionId });
    }, []);

    useEffect(() => {
        // Wait for the first balance to arrive: whether to claim depends on
        // `localCreditEnabled`, which comes with it.
        if (!ret || loading) return undefined;

        let cancelled = false;
        let attempts = 0;

        const settled = (balance) => Boolean(balance) && Number(balance.lifetime_topped_up_usd) > 0;

        const poll = async () => {
            if (cancelled) return;
            const balance = await refresh();
            attempts += 1;

            // The webhook has landed once the lifetime top-up total has moved.
            if (settled(balance)) {
                setState('done');
                return;
            }
            if (attempts >= POLL_ATTEMPTS) {
                setState('slow');
                return;
            }
            setTimeout(poll, POLL_DELAY_MS);
        };

        const start = async () => {
            if (localCreditEnabled && ret.sessionId) {
                try {
                    const idToken = await getAuth().currentUser.getIdToken();
                    // Idempotent on the payment, so a repeat (React re-running
                    // this in development, a later webhook) credits once.
                    const { status, myJson } = await ClaimTopUp(idToken, ret.sessionId);
                    if (cancelled) return;
                    if (status === 200 && settled(myJson)) {
                        await refresh();
                        setState('done');
                        return;
                    }
                } catch (e) {
                    // Fall through to the poll: the webhook may still arrive,
                    // and a failed claim must not swallow a real payment.
                }
            }
            poll();
        };

        start();
        return () => { cancelled = true; };
    }, [ret, loading, localCreditEnabled, refresh]);

    if (!state) return null;

    const dismiss = () => setState(null);

    const copy = {
        confirming: {
            title: 'Confirming your payment…',
            body: 'Stripe is confirming the payment. Your balance updates automatically — this usually takes a few seconds.'
        },
        done: {
            title: 'Funds added',
            body: 'Your balance is updated and ready to use.'
        },
        slow: {
            title: 'Payment is still confirming',
            body: 'Stripe has not confirmed the payment yet. It will appear in your balance shortly — no action needed, and you have not been charged twice.'
        },
        cancelled: {
            title: 'Top-up cancelled',
            body: 'Nothing was charged. You can add funds any time from the balance in the top bar.'
        }
    }[state];

    return (
        <div className="wallet-modal-backdrop" onClick={dismiss} role="presentation">
            <div
                className="wallet-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="wallet-return-title"
            >
                <h2 id="wallet-return-title">{copy.title}</h2>
                <p className="wallet-modal-sub">{copy.body}</p>
                <div className="wallet-modal-actions">
                    <button type="button" className="wallet-btn-primary" onClick={dismiss}>
                        {state === 'confirming' ? 'Continue' : 'Done'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export { TopUpReturnHandler };
